import React, { Component } from 'react';
import { NotificationsContext } from '../context/NotificationsContext';
import Navbar from '../components/Navbar';
import NotificationIcon from '../components/NotificationIcon';
import LoadingSpinner from '../components/LoadingSpinner';

class Notifications extends Component {
  static contextType = NotificationsContext;

  constructor(props) {
    super(props);
    this.state = {
      filter: 'all', // 'all', 'unread', 'read'
      searchQuery: '',
    };
  }

  getFilteredNotifications = () => {
    const { notifications } = this.context;
    const { filter, searchQuery } = this.state;

    let filtered = [...notifications];

    // Filter by read status
    if (filter === 'unread') {
      filtered = filtered.filter(n => !n.read);
    } else if (filter === 'read') {
      filtered = filtered.filter(n => n.read);
    }

    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(n =>
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  groupNotificationsByDate = (notifications) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    };

    notifications.forEach(notification => {
      const notifDate = new Date(notification.createdAt);
      const notifDay = new Date(notifDate.getFullYear(), notifDate.getMonth(), notifDate.getDate());

      if (notifDay.getTime() === today.getTime()) {
        groups.today.push(notification);
      } else if (notifDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(notification);
      } else if (notifDay >= lastWeek) {
        groups.thisWeek.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  };

  formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  getNotificationTypeLabel = (type) => {
    const labels = {
      job_shared: 'Job Shared',
      job_created: 'Job Created',
      job_updated: 'Job Updated',
      job_deleted: 'Job Deleted',
      user_invited: 'User Invited',
      invitation_accepted: 'Invitation Accepted',
      user_registered: 'User Registered',
      password_changed: 'Password Changed',
      email_changed: 'Email Changed',
    };

    return labels[type] || type;
  };

  render() {
    const { loading, markAsRead, markAsUnread, markAllAsRead, deleteNotification } = this.context;
    const { filter, searchQuery } = this.state;

    const filteredNotifications = this.getFilteredNotifications();
    const groupedNotifications = this.groupNotificationsByDate(filteredNotifications);
    const hasNotifications = filteredNotifications.length > 0;

    if (loading) {
      return (
        <div className="min-h-screen bg-slate-50">
          <Navbar />
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner size="large" />
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Notifications</h1>
            <p className="text-slate-600 mt-1">Stay updated with your latest activity</p>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
            <div className="p-4 border-b border-slate-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
                {/* Filter Tabs */}
                <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => this.setState({ filter: 'all' })}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                      filter === 'all'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => this.setState({ filter: 'unread' })}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                      filter === 'unread'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Unread
                  </button>
                  <button
                    onClick={() => this.setState({ filter: 'read' })}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                      filter === 'read'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Read
                  </button>
                </div>

                {/* Search and Actions */}
                <div className="flex items-center space-x-3">
                  <input
                    type="text"
                    placeholder="Search notifications..."
                    value={searchQuery}
                    onChange={(e) => this.setState({ searchQuery: e.target.value })}
                    className="px-4 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  {hasNotifications && (
                    <button
                      onClick={markAllAsRead}
                      className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Notifications List */}
            <div>
              {!hasNotifications ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <p className="text-slate-600 text-lg mb-2">No notifications</p>
                  <p className="text-slate-500 text-sm">
                    {searchQuery ? 'No notifications match your search' : "You're all caught up!"}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {/* Today */}
                  {groupedNotifications.today.length > 0 && (
                    <div>
                      <div className="px-6 py-3 bg-slate-50">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Today</h3>
                      </div>
                      {groupedNotifications.today.map(notification => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={markAsRead}
                          onMarkAsUnread={markAsUnread}
                          onDelete={deleteNotification}
                          formatTime={this.formatTime}
                          getTypeLabel={this.getNotificationTypeLabel}
                        />
                      ))}
                    </div>
                  )}

                  {/* Yesterday */}
                  {groupedNotifications.yesterday.length > 0 && (
                    <div>
                      <div className="px-6 py-3 bg-slate-50">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Yesterday</h3>
                      </div>
                      {groupedNotifications.yesterday.map(notification => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={markAsRead}
                          onMarkAsUnread={markAsUnread}
                          onDelete={deleteNotification}
                          formatTime={this.formatTime}
                          getTypeLabel={this.getNotificationTypeLabel}
                        />
                      ))}
                    </div>
                  )}

                  {/* This Week */}
                  {groupedNotifications.thisWeek.length > 0 && (
                    <div>
                      <div className="px-6 py-3 bg-slate-50">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">This Week</h3>
                      </div>
                      {groupedNotifications.thisWeek.map(notification => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={markAsRead}
                          onMarkAsUnread={markAsUnread}
                          onDelete={deleteNotification}
                          formatTime={this.formatTime}
                          getTypeLabel={this.getNotificationTypeLabel}
                        />
                      ))}
                    </div>
                  )}

                  {/* Older */}
                  {groupedNotifications.older.length > 0 && (
                    <div>
                      <div className="px-6 py-3 bg-slate-50">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Older</h3>
                      </div>
                      {groupedNotifications.older.map(notification => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={markAsRead}
                          onMarkAsUnread={markAsUnread}
                          onDelete={deleteNotification}
                          formatTime={this.formatTime}
                          getTypeLabel={this.getNotificationTypeLabel}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }
}

// Notification Item Component
class NotificationItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showActions: false,
    };
  }

  render() {
    const { notification, onMarkAsRead, onMarkAsUnread, onDelete, formatTime, getTypeLabel } = this.props;
    const { showActions } = this.state;

    return (
      <div
        className={`px-6 py-4 hover:bg-slate-50 transition relative ${
          !notification.read ? 'bg-blue-50' : ''
        }`}
        onMouseEnter={() => this.setState({ showActions: true })}
        onMouseLeave={() => this.setState({ showActions: false })}
      >
        <div className="flex items-start space-x-4">
          {/* Icon */}
          <div className="flex-shrink-0 mt-1">
            <NotificationIcon type={notification.type} size="large" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{notification.title}</p>
                <p className="text-sm text-slate-600 mt-1">{notification.message}</p>
                {/* ✅ Add this section */}
                {notification.relatedJobName && notification.jobDeleted && (
                  <span className="inline-block mt-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                    Job Deleted
                  </span>
                )}
                <div className="flex items-center space-x-3 mt-2">
                  <span className="text-xs text-slate-500">{formatTime(notification.createdAt)}</span>
                  <span className="text-xs text-slate-400">•</span>
                  <span className="text-xs text-slate-500">{getTypeLabel(notification.type)}</span>
                </div>
              </div>

              {/* Read indicator */}
              {!notification.read && (
                <div className="flex-shrink-0 ml-2">
                  <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hover Actions */}
        {showActions && (
          <div className="absolute right-4 top-4 flex items-center space-x-2">
            {notification.read ? (
              <button
                onClick={() => onMarkAsUnread(notification.id)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition"
                title="Mark as unread"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                </svg>
              </button>
            ) : (
              <button
                onClick={() => onMarkAsRead(notification.id)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition"
                title="Mark as read"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            <button
              onClick={() => onDelete(notification.id)}
              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default Notifications;